import React from 'react'
import {
  Icon,
  Card,
  Field,
  FieldGroup,
  Label,
  RadioGroup,
  CriticalRadio,
  Button,
  Input,
  Select,
} from '../../../atoms'
import { getComponentById } from '../../../redux/component/component-selectors'
import { connect } from 'react-redux'
import { FsCore } from '../../../types/fs-core'
import { getBestRate } from './calculation'
import ImgIx from '../../imgix-img'
import TravelOther from './travel-other'
import ProgressCircle from '@uswitch/ev-components/lib/components/elements/progress-circle'
import {
  currencyMappings,
  currencyTypeMappings,
  gbpCurrency,
  mainCurrencyOptions,
  flagMappings,
} from './mappings'
import { Helmet } from 'react-helmet-async'

export type Collection = 'delivery' | 'collection'

export const formatThousands = (n: string | number) =>
  n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

interface TravelMoneyCalculatorDetails {
  currency: string | undefined
}

const TravelMoneyCalculator = ({
  travelMoneyData,
  jsonBody,
  ...props
}: {
  travelMoneyData: any
  jsonBody: TravelMoneyCalculatorDetails | undefined
  currency: string
}) => {
  const fromCurrency = 'GBP'
  const mainCurrency = jsonBody?.currency ?? 'EUR'
  const swapped = false
  const bestRate = getBestRate(750, 750, 'GBP', mainCurrency, 'delivery', travelMoneyData)
  const radioButtons = [
    ['cash', 'Cash'],
    ['money-travel-card', 'Travel money card'],
    ['travel-insurance', 'Travel Insurance'],
    ['prepaid-card', 'Prepaid card'],
  ]
  const collectionType = [
    ['delivery', 'Delivery'],
    ['location', 'Collection'],
  ]

  const dataClickTracking = {
    component_category: 'platinum-module',
    component_id: props.sys?.id,
    component_name: 'travelMoneyCalculator',
    contentful_name: props.name,
    contentful_variant: props.variant,
  }

  return (
    <div data-all-travel-money-content>
      <Helmet>
        <script type='application/json' data-travel-money-script-data>
          {JSON.stringify(travelMoneyData)}
        </script>
      </Helmet>
      <div className='TravelMoneyCalculator' data-tabs-selector>
        <Card variant='plum'>
          <h2 className='TravelMoneyCalculator__title'>Currency Calculator</h2>
          <div className='TravelMoneyCalculator__inner-card'>
            <Label>How would you like your currency?</Label>
            <RadioGroup stack={true}>
              {radioButtons.map((button, index) => {
                return (
                  <CriticalRadio
                    key={index}
                    name={`travel-money-calculator-${props.sys?.id}`}
                    centerLabel={true}
                    buttonAlignment={'top-left'}
                    checked={index === 0}
                    buttonActions={{
                      'data-tabs-selector-radio': index,
                      'data-click-tracking': JSON.stringify({
                        ...dataClickTracking,
                        additionalProps: {
                          name: button[0],
                          index,
                        },
                      }),
                    }}
                  >
                    <div className='TravelMoneyCalculator__currencyType'>
                      <Icon
                        glyph={button[0] === 'money-travel-card' ? 'card-fees' : `${button[0]}`}
                        size={32}
                        color={'#34454E'}
                      />
                      <div className='TravelMoneyCalculator__currencyType__text'>
                        <span className='TravelMoneyCalculator__currencyType__label'>
                          {button[1]}
                        </span>
                      </div>
                    </div>
                  </CriticalRadio>
                )
              })}
            </RadioGroup>
          </div>
          {radioButtons.map((button, index) => {
            if (index === 0) {
              return (
                <div data-tabs-selector-content key={index}>
                  <FieldGroup stack={true}>
                    <div>
                      <Field stack={true}>
                        <Label>Amount</Label>
                        <Input
                          name='amount'
                          value={750}
                          type='text'
                          prefix={
                            currencyMappings.find((c) => c.code === fromCurrency)?.symbol ?? ''
                          }
                          format='thousands'
                          error={false}
                          buttonActions={{
                            'data-travel-input-selector-content': index,
                            'data-click-tracking': JSON.stringify({
                              ...dataClickTracking,
                              additionalProps: {
                                name: button[0],
                                index,
                              },
                            }),
                          }}
                        />
                        <div
                          className='TravelMoneyCalculator__error'
                          data-travel-money-amount-error-content
                        ></div>
                      </Field>
                    </div>
                    <Field stack={true}>
                      <Label>From</Label>
                      <Select
                        name='from-currency'
                        value={fromCurrency}
                        options={swapped ? mainCurrencyOptions : gbpCurrency}
                        styles={{
                          backgroundImage: `url(/fs-core-assets/icons/money/${flagMappings[fromCurrency]}.svg?v=13#${flagMappings[fromCurrency]}), url(/fs-core-assets/icons/money/chevron-down.svg?v=13#chevron-down)`,
                          backgroundPosition: '10px,calc(100% - 10px) 50%',
                          backgroundSize: '10%,auto',
                          paddingLeft: '48px',
                        }}
                        buttonActions={{
                          'data-travel-select-currency-content': index,
                          'data-click-tracking': JSON.stringify({
                            ...dataClickTracking,
                            additionalProps: {
                              name: button[0],
                              index,
                            },
                          }),
                        }}
                      />
                    </Field>
                    <div className='TravelMoneyCalculator__amountField'>
                      <button
                        data-travel-swap-currency-button
                        className='TravelMoneyCalculator__swapButton'
                      >
                        <Icon glyph={'swap-circle'} size={32} color={'#34454E'} />
                      </button>
                    </div>
                    <Field stack={true}>
                      <Label>To</Label>
                      <Select
                        name='to-currency'
                        value={mainCurrency}
                        options={swapped ? gbpCurrency : mainCurrencyOptions}
                        buttonActions={{
                          'data-travel-select-currency-content': index,
                          'data-click-tracking': JSON.stringify({
                            ...dataClickTracking,
                            additionalProps: {
                              name: button[0],
                              index,
                            },
                          }),
                        }}
                        styles={{
                          backgroundImage: `url(/fs-core-assets/icons/money/${flagMappings[mainCurrency]}.svg?v=13#${flagMappings[mainCurrency]}), url(/fs-core-assets/icons/money/chevron-down.svg?v=13#chevron-down)`,
                          backgroundPosition: '10px,calc(100% - 10px) 50%',
                          backgroundSize: '10%,auto',
                          paddingLeft: '48px',
                        }}
                      />
                    </Field>
                  </FieldGroup>
                  <div
                    className='TravelMoneyCalculator__inner-card'
                    data-travel-collection-selector
                  >
                    <Label>Delivery or Collection?</Label>
                    <div className='TravelMoneyCalculator__collectionContainer'>
                      <RadioGroup stack={true}>
                        {collectionType.map((collection, index) => {
                          return (
                            <CriticalRadio
                              key={index}
                              name={`travel-money-collection-${props.sys?.id}`}
                              checked={index === 0}
                              centerLabel={true}
                              buttonAlignment={'top-left'}
                              buttonActions={{
                                'data-travel-collection-selector-radio': index,
                                'data-click-tracking': JSON.stringify({
                                  ...dataClickTracking,
                                  additionalProps: {
                                    name: collection[0],
                                    index,
                                  },
                                }),
                              }}
                            >
                              <div className='TravelMoneyCalculator__currencyType'>
                                <Icon glyph={collection[0]} size={32} color={'#34454E'} />
                                <div className='TravelMoneyCalculator__currencyType__text'>
                                  <span className='TravelMoneyCalculator__currencyType__label'>
                                    {collection[1]}
                                  </span>
                                </div>
                              </div>
                            </CriticalRadio>
                          )
                        })}
                      </RadioGroup>
                    </div>
                  </div>
                  <div className='TravelMoneyCalculator__conversionData'>
                    <div
                      className='TravelMoneyCalculator__loadingSpinner'
                      data-travel-loading-spinner-content
                    >
                      <ProgressCircle sx={{ maxHeight: '32px' }} />
                    </div>
                    <div className='TravelMoneyError' data-travel-money-error-data>
                      <div className='TravelMoneyError__title'></div>
                      <div className='TravelMoneyError__subtitle'></div>
                    </div>
                    <div
                      className='TravelMoneyCalculator__conversionData__exchange'
                      data-travel-money-best-rate-data
                    >
                      <div className='TravelMoneyCalculator__conversionData__exchange__bestRate'>
                        Best Rate from
                      </div>
                      <div
                        className='TravelMoneyCalculator__conversionData__logo'
                        data-travel-money-logo-data
                      >
                        <ImgIx
                          src={bestRate.logo}
                          width={150}
                          alt={`${bestRate.name} logo`}
                          critical={false}
                        />
                      </div>
                      <div
                        className='TravelMoneyCalculator__conversionData__text'
                        data-travel-money-conversion-text-data
                      >
                        <div>
                          1 {fromCurrency} ={' '}
                          {swapped ? (1 / bestRate.rate).toFixed(3) : bestRate.rate} {mainCurrency}
                        </div>
                        <div>
                          1 {mainCurrency} ={' '}
                          {swapped ? bestRate.rate : (1 / bestRate.rate).toFixed(3)} {fromCurrency}
                        </div>
                      </div>
                    </div>
                    <div
                      className='TravelMoneyCalculator__conversionData__disclaimer'
                      data-travel-money-best-rate-data
                    >
                      {`Amount received ${
                        collectionType[0][0] === 'delivery'
                          ? '(after accounting for delivery fees)'
                          : ''
                      }`}
                    </div>
                    <div
                      className='TravelMoneyCalculator__conversionData__received'
                      data-travel-money-best-rate-data
                    >
                      <span
                        className='TravelMoneyCalculator__conversionData__received__left'
                        data-travel-money-received-left-data
                      ></span>
                      <span
                        className='TravelMoneyCalculator__conversionData__received__right'
                        data-travel-money-received-right-data
                      ></span>
                    </div>
                    <div
                      className='TravelMoneyCalculator__errorState'
                      data-travel-money-no-result-data
                    ></div>
                  </div>
                  <div className='TravelMoneyCalculator__cta' data-travel-money-show-results>
                    <Button
                      variant={'primary'}
                      withIcon={false}
                      href='/travel-money/results?gbpAmount=750&currencyToUse=GBP&currencyPairEUR&fromCurrency=GBP&mainCurrency=EUR&otherAmount=750&collectionType=delivery&swapped=false'
                    >
                      Show Results
                    </Button>
                  </div>
                </div>
              )
            } else {
              return (
                <div
                  data-tabs-selector-content
                  data-show-other-results-content
                  className='TravelMoneyCalculator__otherResults'
                  key={index}
                >
                  <TravelOther
                    text={currencyTypeMappings[button[0]].text}
                    buttonText={currencyTypeMappings[button[0]].buttonText}
                    href={currencyTypeMappings[button[0]].href}
                  />
                </div>
              )
            }
          })}
        </Card>
      </div>
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    travelMoneyData: getComponentById(state, props.sys.id)?.travelMoneyData,
    ...props,
  }
})(TravelMoneyCalculator)
